import { useSetAtom } from "jotai";
import { useParams } from "react-router-dom";
import { useAsync } from "react-use";
import { userTypeAtom } from "../../auth/state";
import { LoadingScreen } from "../../components/LoadingScreen";
import { useIsValidProviderAndPatient } from "../../hooks/useIsValidProviderAndPatient";
import { useGracefullyUpdateStatsigUserCallback } from "../../statsig/useGracefullyUpdateStatsigUserCallback";
import { UserType } from "../../types";
import { useGracefullyAuthorizeCurrentUserCallback } from "../auth/useGracefullyAuthorizeCurrentUserCallback";
import { useGracefullyLoadVisitorSegmentCallback } from "../segment/useGracefullyLoadVisitorSegmentCallback";
import { useInitializeVideoSDKAdapter } from "../../video-sdk/actions/useLifecycleActions";
import { ErrorPage } from "../../error/ErrorPage";
import { useVideoPlatform } from "../../hooks/useVideoPlatform";
import { useGetPublicProvider } from "../../hooks/useGetPublicProvider";
import { gracefullySetLoggerGlobalContext } from "../../datadog/logger";

/**
 * Handles any necessary pre-rendering tasks for the visitor route e.g. updating
 * the Statsig user. These are tasks that need to be done after the visitor user
 * info is available (i.e. route params) and before the visitor route content is
 * rendered.
 */
export function WithVisitorRoutePreRender({
  children,
}: {
  children: React.ReactNode;
}) {
  const setUserType = useSetAtom(userTypeAtom);
  const gracefullyUpdateStatsigUser = useGracefullyUpdateStatsigUserCallback();
  const gracefullyLoadVisitorSegment =
    useGracefullyLoadVisitorSegmentCallback();

  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const getPublicProvider = useGetPublicProvider();
  const { loading: isValidProviderAndPatientLoading } =
    useIsValidProviderAndPatient(providerShortId!, patientShortId!);

  const gracefullyAuthorizeCurrentUser =
    useGracefullyAuthorizeCurrentUserCallback();
  const initializeVideoSDKAdapter = useInitializeVideoSDKAdapter();
  const [, getProviderVideoPlatform] = useVideoPlatform();

  const { loading, error } = useAsync(
    async function loadBeforeRender() {
      gracefullySetLoggerGlobalContext({ patientShortId, providerShortId });

      setUserType(UserType.CLIENT);
      const [_, publicProviderResult] = await Promise.allSettled([
        gracefullyLoadVisitorSegment({
          providerShortId,
          patientShortId,
        }),
        // initialize Statsig with a Provider's State of Operation to allow more
        // targeted rollout of Zoom vs. Twilio
        getPublicProvider(providerShortId ?? ""),
      ]);

      const publicProvider =
        publicProviderResult.status === "fulfilled"
          ? publicProviderResult.value.data
          : undefined;

      await gracefullyUpdateStatsigUser({
        userID: patientShortId,
        customIDs: {
          providerShortID: providerShortId,
        },
        custom: {
          providerUsState: publicProvider?.state || "UNKNOWN",
          userType: UserType.CLIENT,
        },
      });
      // Logic that depends on the Statsig user update 👇

      // Video SDK initialization error should be blocking
      await initializeVideoSDKAdapter(getProviderVideoPlatform());

      await gracefullyAuthorizeCurrentUser({
        variables: { patientShortId: patientShortId ?? "" },
      });
    },
    [
      gracefullyUpdateStatsigUser,
      gracefullyAuthorizeCurrentUser,
      patientShortId,
      providerShortId,
    ],
  );

  if (loading || isValidProviderAndPatientLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return children;
}
